import React, { useState, useEffect } from 'react';
import { 
  FaStar, 
  FaRegStar,
  FaUser,
  FaEnvelope,
  FaComment,
  FaCheckCircle,
  FaGoogle,
  FaExternalLinkAlt,
  FaCopy,
  FaWhatsapp,
  FaEnvelopeOpen
} from 'react-icons/fa';

const GoogleReviewSystem = () => {
  // Replace with your Google Apps Script URL
  const GOOGLE_SCRIPT_URL = 'https://script.google.com/macros/s/AKfycbzT6yoEmGKXICvBvm8GicVVIPE0_Dc6TL9ol0Rmxrx0nAwTVl8VIEjfFt0cB4Zhbb9A/exec';
  
  // IMPORTANT: Replace with your actual Google Place ID
  // Get it from: https://developers.google.com/maps/documentation/places/web-service/place-id
  const GOOGLE_PLACE_ID = 'ChIJATGOU5lDsjsRCWb1EIt-ywo';
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    rating: 0,
    comment: '',
    course: ''
  });

  const [hoverRating, setHoverRating] = useState(0);
  const [reviews, setReviews] = useState([]);
  const [showSuccess, setShowSuccess] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [googleReviewLink, setGoogleReviewLink] = useState('');
  const [showLinkModal, setShowLinkModal] = useState(false);

  const courses = [
    'IIT-JEE', 'NEET', 'CA', 'GATE', 'Web Development', 'Data Science',
    'Machine Learning', 'Cloud Computing', 'Cyber Security', 'Other'
  ];

  useEffect(() => {
    loadReviews();
    generateGoogleReviewLink();
  }, []);

  const generateGoogleReviewLink = () => {
    // Google Review Link Format
    const link = `https://search.google.com/local/writereview?placeid=${GOOGLE_PLACE_ID}`;
    setGoogleReviewLink(link);
  };

  const loadReviews = async () => {
    try {
      const response = await fetch(`${GOOGLE_SCRIPT_URL}?action=getReviews`);
      const data = await response.json();
      if (data.success) {
        setReviews(data.reviews.sort((a, b) => b.timestamp - a.timestamp));
      }
    } catch (error) {
      console.log('Error loading reviews');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleRatingClick = (rating) => {
    setFormData(prev => ({ ...prev, rating }));
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    alert('Google Review link copied to clipboard!');
  };

  const sendReviewRequest = async () => {
    if (!formData.name || !formData.email || !formData.phone) {
      alert('Please fill in name, email, and phone number');
      return;
    }

    setSubmitting(true);

    try {
      const requestData = {
        action: 'sendReviewRequest',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        googleReviewLink: googleReviewLink,
        timestamp: Date.now(),
        date: new Date().toLocaleDateString('en-IN')
      };

      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestData)
      });

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      setFormData({ name: '', email: '', phone: '', rating: 0, comment: '', course: '' });
    } catch (error) {
      alert('Failed to send review request');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitInternal = async () => {
    if (!formData.name || !formData.email || !formData.rating || !formData.comment) {
      alert('Please fill in all required fields');
      return;
    }

    setSubmitting(true);

    try {
      const reviewData = {
        action: 'addInternalReview',
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        rating: formData.rating,
        comment: formData.comment,
        course: formData.course,
        timestamp: Date.now(),
        date: new Date().toLocaleDateString('en-IN')
      };

      await fetch(GOOGLE_SCRIPT_URL, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(reviewData)
      });

      setTimeout(() => loadReviews(), 2000);
      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 5000);
      setFormData({ name: '', email: '', phone: '', rating: 0, comment: '', course: '' });
    } catch (error) {
      alert('Failed to submit review');
    } finally {
      setSubmitting(false);
    }
  };

  const renderStars = (rating, clickable = false, size = '1.5rem') => {
    return (
      <div className="d-inline-flex">
        {[1, 2, 3, 4, 5].map((star) => (
          <span
            key={star}
            onClick={() => clickable && handleRatingClick(star)}
            onMouseEnter={() => clickable && setHoverRating(star)}
            onMouseLeave={() => clickable && setHoverRating(0)}
            style={{
              cursor: clickable ? 'pointer' : 'default',
              color: star <= (hoverRating || rating) ? '#ffc107' : '#e4e5e9',
              fontSize: size,
              marginRight: '0.25rem'
            }}
          >
            {star <= (hoverRating || rating) ? <FaStar /> : <FaRegStar />}
          </span>
        ))}
      </div>
    );
  };

  const getAverageRating = () => {
    if (reviews.length === 0) return 0;
    const sum = reviews.reduce((acc, r) => acc + r.rating, 0);
    return (sum / reviews.length).toFixed(1);
  };

  return (
    <div className="min-vh-100" style={{ backgroundColor: '#f8f9fa' }}>
      <div className="bg-primary text-white py-5 my-5">
        <div className="container text-center">
          <img src="https://www.keyweo.com/wp-content/uploads/2022/04/google-logo-history.jpg" alt="google"className='img-fluid mx-100 w-25 h-25 rounded-3 border-3 mb-2' />
          <h1 className="display-4 fw-bold mb-3">Google Reviews System</h1>
          <p className="lead">Help us grow by sharing your experience on Google</p>
        </div>
      </div>

      {showSuccess && (
        <div className="container mt-4">
          <div className="alert alert-success alert-dismissible fade show" role="alert">
            <FaCheckCircle className="me-2" />
            <strong>Success!</strong> Review request sent successfully.
            <button type="button" className="btn-close" onClick={() => setShowSuccess(false)}></button>
          </div>
        </div>
      )}

      <div className="container py-5">
        {/* Google Review Link Display */}
        <div className="row mb-5">
          <div className="col-lg-8 mx-auto">
            <div className="card border-0 shadow-lg">
              <div className="card-body p-4">
                <h3 className="h4 mb-3">
                  <FaGoogle className="me-2 text-danger" />
                  Direct Google Review Link
                </h3>
                <p className="text-muted mb-3">Share this link with your students to collect Google reviews</p>
                
                <div className="input-group mb-3">
                  <input 
                    type="text" 
                    className="form-control" 
                    value={googleReviewLink}
                    readOnly
                  />
                  <button 
                    className="btn btn-outline-primary"
                    onClick={() => copyToClipboard(googleReviewLink)}
                  >
                    <FaCopy className="me-2" /> Copy Link
                  </button>
                </div>

                <div className="d-flex gap-2 flex-wrap">
                  <a 
                    href={googleReviewLink} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="btn btn-danger"
                  >
                    <FaGoogle className="me-2" /> Leave Google Review
                  </a>
                  
                  <button 
                    className="btn btn-success"
                    onClick={() => window.open(`https://wa.me/?text=Please share your review for Olewin Academy: ${encodeURIComponent(googleReviewLink)}`, '_blank')}
                  >
                    <FaWhatsapp className="me-2" /> Share via WhatsApp
                  </button>
                </div>

                <div className="alert alert-info mt-3 mb-0">
                  <small>
                    <strong>Note:</strong> Students need a Google account to leave a review. 
                    Reviews will appear on Google Maps and Search within 24-48 hours.
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Two Options */}
        <div className="row mb-4">
          <div className="col-md-6 mb-3">
            <div className="card h-100 border-primary">
              <div className="card-body text-center p-4">
                <FaEnvelopeOpen style={{ fontSize: '3rem', color: '#0d6efd' }} className="mb-3" />
                <h4>Option 1: Send Review Request</h4>
                <p className="text-muted">Send an email with Google review link to a student</p>
              </div>
            </div>
          </div>
          <div className="col-md-6 mb-3">
            <div className="card h-100 border-success">
              <div className="card-body text-center p-4">
                <FaStar style={{ fontSize: '3rem', color: '#198754' }} className="mb-3" />
                <h4>Option 2: Collect Internal Review</h4>
                <p className="text-muted">Store reviews in your database (display on website)</p>
              </div>
            </div>
          </div>
        </div>

        {/* Send Review Request Form */}
        <div className="row mb-5">
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h3 className="h5 mb-4">
                  <FaEnvelopeOpen className="me-2 text-primary" />
                  Send Google Review Request
                </h3>
                
                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <FaUser className="me-2" />Student Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter student name"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <FaEnvelope className="me-2" />Email <span className="text-danger">*</span>
                  </label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="student@example.com"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Phone Number <span className="text-danger">*</span></label>
                  <input
                    type="tel"
                    className="form-control"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="10-digit mobile"
                    maxLength="10"
                  />
                </div>

                <button 
                  className="btn btn-primary w-100"
                  onClick={sendReviewRequest}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Sending...
                    </>
                  ) : (
                    <>
                      <FaEnvelopeOpen className="me-2" />
                      Send Review Request Email
                    </>
                  )}
                </button>

                <div className="alert alert-warning mt-3 mb-0">
                  <small>This will send an email with the Google review link to the student</small>
                </div>
              </div>
            </div>
          </div>

          {/* Internal Review Form */}
          <div className="col-lg-6 mb-4">
            <div className="card border-0 shadow-sm h-100">
              <div className="card-body p-4">
                <h3 className="h5 mb-4">
                  <FaStar className="me-2 text-success" />
                  Collect Internal Review
                </h3>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <FaUser className="me-2" />Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    placeholder="Enter name"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Email</label>
                  <input
                    type="email"
                    className="form-control"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="email@example.com"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">Course</label>
                  <select
                    className="form-select"
                    name="course"
                    value={formData.course}
                    onChange={handleInputChange}
                  >
                    <option value="">Select course</option>
                    {courses.map(course => (
                      <option key={course} value={course}>{course}</option>
                    ))}
                  </select>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <FaStar className="me-2" />Rating <span className="text-danger">*</span>
                  </label>
                  <div className="p-3 bg-light rounded text-center">
                    {renderStars(formData.rating, true, '2rem')}
                  </div>
                </div>

                <div className="mb-3">
                  <label className="form-label fw-semibold">
                    <FaComment className="me-2" />Review <span className="text-danger">*</span>
                  </label>
                  <textarea
                    className="form-control"
                    name="comment"
                    value={formData.comment}
                    onChange={handleInputChange}
                    rows="4"
                    placeholder="Share your experience..."
                  ></textarea>
                </div>

                <button 
                  className="btn btn-success w-100"
                  onClick={handleSubmitInternal}
                  disabled={submitting}
                >
                  {submitting ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2"></span>
                      Submitting...
                    </>
                  ) : (
                    <>
                      <FaCheckCircle className="me-2" />
                      Submit Internal Review
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Display Reviews */}
        {reviews.length > 0 && (
          <div className="row">
            <div className="col-12">
              <div className="text-center mb-4">
                <h2 className="h3 mb-3">Our Reviews</h2>
                <div className="d-inline-flex align-items-center gap-3 bg-light p-3 rounded">
                  <div>
                    <div className="display-6 text-warning">{getAverageRating()}</div>
                    {renderStars(Math.round(parseFloat(getAverageRating())))}
                  </div>
                  <div className="text-start">
                    <div className="fw-bold">{reviews.length} Reviews</div>
                    <small className="text-muted">Average Rating</small>
                  </div>
                </div>
              </div>

              <div className="row g-4">
                {reviews.map((review, index) => (
                  <div key={index} className="col-md-6 col-lg-4">
                    <div className="card h-100 border-0 shadow-sm">
                      <div className="card-body">
                        <div className="d-flex justify-content-between mb-3">
                          <h5 className="mb-0">{review.name}</h5>
                          {renderStars(review.rating, false, '1rem')}
                        </div>
                        {review.course && (
                          <span className="badge bg-primary mb-2">{review.course}</span>
                        )}
                        <p className="mb-2">{review.comment}</p>
                        <small className="text-muted">{review.date}</small>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>

      <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet" />
    </div>
  );
};

export default GoogleReviewSystem;